<script lang="ts">
	import Button from '#components/button.svelte'
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Actions } from '#library/components.schema.build.ts'
	import Render from '#components/render.svelte'
	import Image from '#components/image.svelte'
	import Shape from '#components/shape.svelte'
	import { ui } from '#library/stores.ts'
	import Field from '#components/field.svelte'

	interface Props {
		blok: Blok<Actions>
	}

	let { blok }: Props = $props()
	let { items, image, shape } = $derived(blok)
	let Figure = $derived(shape ? Shape : Image)
</script>

<section class="actions {blok.theme || 'theme-pink-on-lupton'}" class:accent={blok.accent} {...attrs(blok)}>
	<div
		class="contain"
		class:accent={blok.accent}
		class:alt={blok.alt}
		class:even={items?.length == 1 && image?.filename}
		class:illustration={blok.illustration}
	>
		<div class="content" class:alt={blok.alt}>
			<div class="title" class:cap={image?.filename}>{blok.title}</div>
			{#if blok.subtitle}
				<div class="subtitle">
					<Render content={blok.subtitle} />
				</div>
			{/if}
			{#if blok.subscribe}
				<form class="form">
					<Field
						name="email"
						type="email"
						id="email"
						placeholder="Enter your email"
						bind:value={$ui.email}
						inline
					/>
					<Button type="button" onclick={() => ($ui.subscribe = true)}>Subscribe</Button>
				</form>
			{/if}
			{#if items?.length == 1 && items[0].link?.url && (image?.filename || blok.illustration)}
				<div class="button">
					<Button href={items[0].link.url} outline={!blok.alt} large icon="arrow-right" fill>
						{items[0].link.text ?? ''}
					</Button>
				</div>
			{/if}
		</div>
		{#if image?.filename}
			<figure class="figure" class:alt={items?.length == 1}>
				<Figure
					src={image.filename}
					alt={image.alt}
					focus={image.focus}
					name={shape}
					aspect={items?.length == 1 ? '4/3' : '1/1'}
				/>
			</figure>
		{:else if items?.length}
			<div class="items" class:alt={blok.alt}>
				{#each items as item, i (i)}
					{#if item.link?.url}
						<Button
							href={item.link.url}
							outline={!blok.alt}
							large
							icon={items.length == 1 ? 'arrow-up-right' : undefined}
						>
							{item.link.text ?? ''}
						</Button>
					{/if}
				{/each}
			</div>
		{/if}
	</div>
</section>

<style lang="css">
	.actions {
		background-image: var(--pattern);
		padding-inline: 6vw;
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));

		@media (--from-mobile) {
			padding-inline: 5vw;
		}

		&.accent {
			background-image: none;

			&::after {
				background-image: var(--pattern);
				content: '';
				display: block;
				height: 1.5rem;
				margin-inline: auto;
				max-width: var(--s-wrap);
				width: 92vw;

				@media (--from-mobile) {
					width: 90vw;
				}
			}
		}
	}

	.contain {
		background-color: var(--c-background);
		color: var(--c-midground);
		overflow: clip;
		padding-block: 2.25rem 1.75rem;
		padding-inline: 1.5rem;

		@media (--from-mobile) {
			padding-block: 3rem;
			padding-inline: 5rem;
		}

		&.alt {
			display: grid;
			gap: 2rem;
			padding-block: 1.5rem;

			@media (--from-mobile) {
				grid-template-columns: 8fr 3fr;
				padding-block: 5rem;
			}
		}

		&.even {
			@media (--from-mobile) {
				grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
			}
		}

		&.illustration::before {
			aspect-ratio: 1;
			background-image: var(--illustration);
			content: '';
			display: block;
			height: 125%;
			inset: 50% auto auto 100%;
			opacity: 0.5;
			position: absolute;
			translate: -55% -52%;
		}
	}

	.content {
		flex: 1 0 100%;
		text-align: center;

		&.alt {
			text-align: start;
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		text-wrap: balance;

		&.cap {
			max-width: 15ch;
		}

		@media (--until-mobile) {
			margin-block-end: 0.5rem;
		}
	}

	/* TODO: query this theme override */
	.actions.theme-gimson-on-lupton .title {
		color: var(--c-gimson);
	}

	.subtitle {
		flex: 1 0 100%;
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-start: 1.25rem;
		max-width: 40ch;

		:global {
			p + p {
				margin-block-start: 2rem;
			}

			a {
				color: var(--c-foreground);
				font-weight: 600;
			}
		}
	}

	.button {
		margin-block-start: 1.25rem;

		@media (--from-mobile) {
			margin-block-start: 3rem;
			max-width: 15rem;
		}
	}

	.figure {
		@media (--until-mobile) {
			order: -1;
		}

		:global(.image) {
			margin-inline-start: auto;
			/* max-width: 17rem; */
		}
	}

	.items {
		display: grid;
		flex: 1 0 100%;
		gap: 0.75rem;
		margin-block-start: 1.5rem;

		@media (--from-mobile) {
			display: flex;
			flex-wrap: wrap;
			margin-block-start: 3.25rem;
		}

		:global(.button) {
			flex: 1 0 100%;
			width: 100%;

			@media (--from-mobile) {
				flex: 1 0 auto;
				max-width: 25%;
				width: 0%;
			}
		}

		&.alt {
			flex-direction: column;
			gap: 1rem;
			justify-content: center;
			margin-block-start: 0;

			:global(.button) {
				width: auto;

				@media (--from-mobile) {
					flex: 0 0 auto;
					max-width: none;
				}
			}
		}
	}

	.form {
		align-items: center;
		display: flex;
		gap: 0.75rem;
		margin-block-start: 1.25rem;
		max-width: 40ch;

		:global(.field) {
			border: 1px solid color-mix(in srgb, var(--c-foreground), transparent 75%);
			width: 100%;
		}
	}
</style>
